import { useState, useEffect, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import { BrowserRouter } from "react-router-dom";
import {
  Container,
  CircularProgress,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppBar } from "./components/app-bar";
import { Drawer } from "./components/drawer";
import { Dialog } from "./components/dialog";
import { Snackbar } from "./components/snackbar";
import "./App.css";
import { loadAppSchema } from "./redux/slices/app";
import { show as showDialog } from "./redux/slices/dialog";
import { check as checkUser } from "./redux/slices/user";
import { show as showSnackbar } from "./redux/slices/snackbar";
import { useAppSelector, useAppDispatch } from "./hooks";
import { Router } from "./routes";
import phone, {
  clearCall,
  loadSIPAccount,
  setCall,
  loadCampaign,
  loadCampaignContact,
  loadContacts,
} from "./redux/slices/phone";

const socketUrl = process.env.REACT_APP_SOCKET || "";

let ioClient = {};

function App() {
  const { app, user } = useSelector((state: any) => state);
  const dispatch = useAppDispatch();

  //const ioClient = useRef();
  // let [ioClient, setIOClient] = useState({});

  useEffect(() => {
    // console.log(`app.loadingStatus -> '%s'`, app.loadingStatus, app);
    switch (app.loadingStatus) {
      case "none": {
        dispatch(loadAppSchema());
        break;
      }
      case "done": {
        document.title = `${app.name} |`;
        if (!user.isLoggedin) {
          // console.log(`app.schema.loginDialogId`, app.schema.loginDialogId);
          if (app.schema.loginDialogId) {
            dispatch(showDialog({ diaglogId: app.schema.loginDialogId }));
          }
        }
        break;
      }
      default:
        break;
    }
  }, [app.loadingStatus]);

  useEffect(() => {
    switch (user.checkingStatus) {
      case "none": {
        dispatch(checkUser());
        break;
      }
      case "done": {
        if (user.isLoggedin) {
          dispatch(loadAppSchema());
          dispatch(loadSIPAccount());

          ioClient = io(socketUrl, {
            transports: ["websocket", "polling", "flashsocket"],
            query: {
              authorization: user.accessToken,
            },
          });

          (ioClient as any).on("connect", () => {
            console.info("[socket] connect");
          });

          (ioClient as any).on("disconnect", () => {
            console.warn("[socket] connect");
          });

          (ioClient as any).on("notification", (event: any) => {
            dispatch(
              showSnackbar({
                type: "info",
                title: "Thông báo mới!",
                content: event.content,
              })
            );
          });

          (ioClient as any).on("message", (event: any) => {
            dispatch(
              showSnackbar({
                type: "info",
                title: "Tin nhắn mới!",
                content: event.message,
              })
            );
          });

          (ioClient as any).on("call", (event: any) => {
            console.info(`call event from socket`, event);
            if (
              ["ringing", "answering", "hangup", "canceling"].indexOf(event.state) >=
              0
            ) {
              dispatch(
                setCall({
                  direction: event.direction,
                  fromNumber: event.fromNumber,
                  fromSIPAccountId: event.fromSIPAccountId,
                  fromUserId: event.fromUserId,
                  toNumber: event.toNumber,
                  toSIPAccountId: event.toSIPAccountId,
                  toUserId: event.toUserId,
                  startedAt: event.startedAt,
                  answeredAt: event.answeredAt,
                  state: event.state,
                  duration: 0,
                  answeredDuration: 0,
                  campaignContactId: event.campaignContactId,
                  campaignId: event.campaignId,
                })
              );
              if (event.direction === 'inbound'){
                dispatch(loadContacts(event.fromNumber)); 
              }
              if (
                event.campaignContactId !== ""
              ) {
                dispatch(loadCampaignContact(event.campaignContactId));
              }
              if (
                event.campaignId !== ""
              ) {
                dispatch(loadCampaign(event.campaignId));
              }
              switch (event.direction) {
                case "inbound": {
                  if (event.state === "ringing") {
                    dispatch(
                      showSnackbar({
                        type: "info",
                        message: `Cuộc gọi vào từ '${event.fromNumber}'`,
                      })
                    );
                  }

                  break;
                }
                case "outbound": {
                  if (event.state === "ringing") {
                    dispatch(
                      showSnackbar({
                        type: "info",
                        message: `Đang gọi ra tới '${event.toNumber}'`,
                      })
                    );
                  }
                  break;
                }
                default:
                  break;
              }
            }
            /*  else {
              dispatch(clearCall());
            } */
          });
        }
        break;
      }
    }
  }, [user.checkingStatus]);

  useEffect(() => {
    return () => {
      if ((ioClient as any).disconnect) {
        (ioClient as any).disconnect();
      }
    };
  }, []);
  const theme = createTheme(app.schema.theme || {});
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Container className="app-container" maxWidth={false}>
          <Box
            className="overlay"
            sx={{ display: app.loadingStatus === "loading" ? "block" : "none" }}
          >
            <CircularProgress className="loading-image" />
          </Box>
          <Snackbar />
          <AppBar />
          <Drawer />
          <Dialog />
          <Router navigationItems={app.navigationItems} />
        </Container>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
